import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { LogoIcon, LogoText } from '../components/Logo';
import LanguageSelector from '../components/LanguageSelector';
import { useTranslation } from 'react-i18next';

// Variant B: Bold & Sporty
// High energy, dynamic animations, strong visual impact, sports-first design

function LandingB() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const { t, i18n } = useTranslation(['landing', 'common']);

  return (
    <div className="min-h-screen bg-anthracite-950 overflow-hidden">
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('landing:metaTitle')}</title>
        <meta name="description" content={t('landing:metaDesc')} />
        <link rel="canonical" href="https://scorinova.com/" />
      </Helmet>
      <style>{`
        .reveal {
          opacity: 0;
          transform: translateY(50px) scale(0.97);
          transition: opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .reveal.revealed {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
        .reveal-d1 { transition-delay: 0.05s; }
        .reveal-d2 { transition-delay: 0.15s; }
        .reveal-d3 { transition-delay: 0.25s; }
        .reveal-d4 { transition-delay: 0.35s; }
        .reveal-d5 { transition-delay: 0.45s; }

        @keyframes kick {
          0% { transform: translateX(-100%) rotate(-5deg); opacity: 0; }
          60% { transform: translateX(5%) rotate(1deg); opacity: 1; }
          100% { transform: translateX(0) rotate(0deg); opacity: 1; }
        }
        @keyframes slide-up-bounce {
          0% { transform: translateY(60px); opacity: 0; }
          70% { transform: translateY(-8px); opacity: 1; }
          100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes scoreboard-flash {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        @keyframes diagonal-wipe {
          from { clip-path: polygon(0 0, 0 0, 0 100%, 0 100%); }
          to { clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%); }
        }
        @keyframes stripe-scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        @keyframes count-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        .kick-in { animation: kick 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .slide-bounce { animation: slide-up-bounce 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }

        .stripe-banner {
          background: linear-gradient(135deg, #ff6b2c 0%, #e55a1f 100%);
        }
        .stripe-scroll {
          animation: stripe-scroll 20s linear infinite;
        }

        .hero-diagonal {
          clip-path: polygon(0 0, 100% 0, 100% 85%, 0 100%);
        }
        .diagonal-section {
          clip-path: polygon(0 5%, 100% 0, 100% 95%, 0 100%);
        }

        .sport-card {
          background: linear-gradient(135deg, rgba(18, 18, 20, 0.8), rgba(26, 26, 30, 0.6));
          border: 1px solid rgba(255, 107, 44, 0.1);
          transition: all 0.3s ease;
        }
        .sport-card:hover {
          border-color: rgba(255, 107, 44, 0.4);
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(255, 107, 44, 0.1);
        }

        .accent-line {
          background: linear-gradient(90deg, transparent, #ff6b2c, transparent);
          height: 2px;
        }

        .big-number {
          font-size: clamp(3rem, 8vw, 6rem);
          font-weight: 800;
          line-height: 1;
          background: linear-gradient(180deg, #ff6b2c, #e55a1f);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-anthracite-950/90 backdrop-blur-md border-b border-anthracite-800">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <LogoIcon className="w-10 h-10" />
            <LogoText size="text-xl" />
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className="px-4 py-2 text-sm text-gray-400 hover:text-white font-medium transition-colors">
              {t('common:signIn')}
            </Link>
            <Link to="/signup" className="px-5 py-2.5 text-sm font-bold bg-accent hover:bg-accent-dark text-white rounded-lg transition-all hover:scale-105 shadow-lg shadow-accent/25">
              {t('common:joinFree')}
            </Link>
            <LanguageSelector compact />
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center hero-diagonal bg-gradient-to-br from-anthracite-950 via-anthracite-900 to-anthracite-950">
        {/* Dynamic background elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Large diagonal accent stripe */}
          <div className="absolute -top-20 -right-20 w-[600px] h-[600px] bg-accent/[0.04] rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-accent/[0.03] rounded-full blur-3xl" />

          {/* Pitch lines pattern */}
          <svg className="absolute inset-0 w-full h-full opacity-[0.02]" viewBox="0 0 800 600">
            <rect x="50" y="50" width="700" height="500" fill="none" stroke="white" strokeWidth="2"/>
            <line x1="400" y1="50" x2="400" y2="550" stroke="white" strokeWidth="2"/>
            <circle cx="400" cy="300" r="80" fill="none" stroke="white" strokeWidth="2"/>
            <rect x="50" y="175" width="150" height="250" fill="none" stroke="white" strokeWidth="2"/>
            <rect x="600" y="175" width="150" height="250" fill="none" stroke="white" strokeWidth="2"/>
          </svg>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 pt-24 pb-32 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text */}
            <div>
              <div className="reveal inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 border border-accent/20 mb-6">
                <span className="text-xs font-bold text-accent uppercase tracking-wider">{t('landing:aiPowered')}</span>
              </div>

              <h1 className="reveal reveal-d1 text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-[1.05] tracking-tight mb-6">
                {t('landing:yourEdge')}<br />
                {t('landing:starts')}<br />
                <span className="text-accent">{t('landing:here')}</span>
              </h1>

              <p className="reveal reveal-d2 text-lg text-gray-400 mb-8 max-w-md leading-relaxed">
                {t('landing:boldHeroSubtitle')}
              </p>

              <div className="reveal reveal-d3 flex flex-wrap gap-4">
                <Link
                  to="/signup"
                  className="group px-8 py-4 bg-accent hover:bg-accent-dark text-white font-bold rounded-xl transition-all shadow-xl shadow-accent/25 hover:shadow-accent/40 hover:scale-105 flex items-center gap-3 text-lg"
                >
                  {t('common:getStarted')}
                  <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>
                <a href="#preview" className="px-8 py-4 text-gray-400 hover:text-white font-bold rounded-xl border border-anthracite-700 hover:border-accent/30 transition-all text-lg">
                  {t('landing:seePreview')}
                </a>
              </div>
            </div>

            {/* Right: Scoreboard-style cards */}
            <div className="relative">
              {/* Stacked match cards */}
              <div className="space-y-4">
                {[
                  { home: 'Bayern Munich', away: 'Borussia Dortmund', league: 'Bundesliga', pick: 'BTTS Yes', grade: 'A+', time: '20:30' },
                  { home: 'Arsenal', away: 'Liverpool', league: 'Premier League', pick: 'Over 2.5', grade: 'A', time: '17:30' },
                  { home: 'Barcelona', away: 'Real Madrid', league: 'La Liga', pick: 'Home Win', grade: 'A+', time: '21:00' },
                  { home: 'Juventus', away: 'AC Milan', league: 'Serie A', pick: 'Draw', grade: 'B', time: '18:00' },
                ].map((match, i) => (
                  <div key={i} className={`reveal reveal-d${i + 1} sport-card rounded-xl p-4 flex items-center gap-4`}>
                    {/* Time */}
                    <div className="text-center min-w-[48px]">
                      <div className="text-xs text-gray-500 font-mono">{match.time}</div>
                    </div>

                    {/* Divider */}
                    <div className="w-px h-10 bg-anthracite-700" />

                    {/* Teams */}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-white truncate">{match.home}</div>
                      <div className="text-sm text-gray-400 truncate">{match.away}</div>
                      <div className="text-[10px] text-gray-600 uppercase tracking-wider mt-0.5">{match.league}</div>
                    </div>

                    {/* Pick + Grade */}
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <div className="text-sm font-bold text-accent">{match.pick}</div>
                      </div>
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm ${
                        match.grade === 'A+' ? 'bg-emerald-500/20 text-emerald-400' :
                        match.grade === 'A' ? 'bg-green-500/20 text-green-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {match.grade}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Fade out at bottom */}
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-anthracite-950 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Scrolling ticker banner */}
      <div className="stripe-banner py-3 overflow-hidden">
        <div className="stripe-scroll flex items-center gap-8 whitespace-nowrap">
          {[...Array(2)].map((_, setIdx) => (
            <div key={setIdx} className="flex items-center gap-8">
              {['Premier League', 'La Liga', 'Bundesliga', 'Serie A', 'Ligue 1', 'Eredivisie', 'Championship', 'Liga NOS', 'Super Lig', 'Ekstraklasa', 'Pro League', 'EFL League One', 'EFL League Two', '2. Bundesliga', '3. Liga', 'Serie B', 'Ligue 2', 'Segunda Division', 'Scottish Premiership', 'Scottish Championship', 'France National'].map((league, i) => (
                <span key={`${setIdx}-${i}`} className="flex items-center gap-3 text-white/90 font-bold text-sm">
                  {league}
                  <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Stats bar */}
      <section className="py-20 px-6 bg-anthracite-900/50">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { number: '7', label: t('landing:predictionMarkets') },
            { number: '20+', label: t('landing:europeanLeagues') },
            { number: '100+', label: t('landing:dailyPicks') },
            { number: '24/7', label: t('landing:liveTracking') },
          ].map((stat, i) => (
            <div key={i} className={`reveal reveal-d${i + 1} text-center`}>
              <div className="big-number">{stat.number}</div>
              <div className="text-gray-400 text-sm font-medium mt-2">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Markets Grid */}
      <section id="preview" className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="reveal text-center mb-16">
            <h2 className="text-3xl sm:text-5xl font-extrabold text-white">
              {t('landing:everyMarket')}<br />
              <span className="text-accent">{t('landing:everyDay')}</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { name: t('landing:marketOutcome'), desc: t('landing:marketOutcomeDescB'), tag: '1X2', color: 'from-blue-500/10 to-blue-600/5 border-blue-500/10 hover:border-blue-500/30' },
              { name: t('landing:marketBTTS'), desc: t('landing:marketBTTSDescB'), tag: 'BTTS', color: 'from-accent/10 to-orange-600/5 border-accent/10 hover:border-accent/30' },
              { name: t('landing:marketOver25'), desc: t('landing:marketOver25DescB'), tag: 'O2.5', color: 'from-purple-500/10 to-purple-600/5 border-purple-500/10 hover:border-purple-500/30' },
              { name: t('landing:marketDrawSpecialist'), desc: t('landing:marketDrawDescB'), tag: 'DRW', color: 'from-cyan-500/10 to-cyan-600/5 border-cyan-500/10 hover:border-cyan-500/30' },
              { name: t('landing:marketAIConsensus'), desc: t('landing:marketAIDescB'), tag: 'AI', color: 'from-emerald-500/10 to-emerald-600/5 border-emerald-500/10 hover:border-emerald-500/30' },
            ].map((market, i) => (
              <div key={i} className={`reveal reveal-d${Math.min(i + 1, 3)} rounded-2xl p-6 bg-gradient-to-br ${market.color} border transition-all group`}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-bold text-gray-500 font-mono uppercase">{market.tag}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{market.name}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{market.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 px-6 bg-anthracite-900/30">
        <div className="max-w-4xl mx-auto">
          <div className="reveal text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              {t('landing:threeSteps')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: t('landing:step1Title'), desc: t('landing:step1Desc') },
              { step: '02', title: t('landing:step2Title'), desc: t('landing:step2Desc') },
              { step: '03', title: t('landing:step3Title'), desc: t('landing:step3Desc') },
            ].map((item, i) => (
              <div key={i} className={`reveal reveal-d${i + 1} text-center`}>
                <div className="text-5xl font-extrabold text-accent/20 mb-4">{item.step}</div>
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 px-6 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-accent/[0.04] to-transparent" />
        <div className="reveal relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-4 leading-tight">
            {t('landing:dontWatch')}<br />{t('landing:theSidelines')}
          </h2>
          <p className="text-lg text-gray-400 mb-8 max-w-xl mx-auto">
            {t('landing:joinThousands')}
          </p>
          <Link
            to="/signup"
            className="group inline-flex items-center gap-3 px-10 py-5 bg-accent hover:bg-accent-dark text-white font-bold text-lg rounded-xl transition-all shadow-xl shadow-accent/25 hover:shadow-accent/40 hover:scale-105"
          >
            {t('landing:getStartedFree')}
            <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-anthracite-800 py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <LogoText size="text-sm" />
            <span className="text-xs text-gray-600">{t('common:aiFootballPredictions')}</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-gray-600">
            <Link to="/terms" className="hover:text-accent transition-colors">{t('common:terms')}</Link>
            <Link to="/privacy" className="hover:text-accent transition-colors">{t('common:privacyPolicy')}</Link>
            <Link to="/login" className="hover:text-accent transition-colors">{t('common:signIn')}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingB;
